import React, { useEffect, useState } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { getImageUrl } from "../../../utils/imageHelper";
import StyleControls from "./StyleControls";

/**
 * 2COL_3ROWS_Benefits
 * Displays a title, description, and 3 product rows with alternating image positions.
 * Each product shows: image, title, description (semicolon-separated benefits as bullets), and a "Buy Now" button.
 * 
 * Layout:
 * - Row 1: Image left, Content right
 * - Row 2: Content left, Image right
 * - Row 3: Image left, Content right
 */
const TwoColThreeRowsBenefits = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  // In preview: use data/props; in edit mode: use context state
  // Also check data/props for streaming API data
  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || data || props || {};

  const {
    title = "",
    description = "",
    features = [],
    component_name = "2COL_3ROWS_Benefits",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localFeatures, setLocalFeatures] = useState(
    Array.isArray(features) && features.length > 0
      ? features
      : [
          { title: "", description: "", image_placeholder: "", link: "", cta: "Buy Now" },
          { title: "", description: "", image_placeholder: "", link: "", cta: "Buy Now" },
          { title: "", description: "", image_placeholder: "", link: "", cta: "Buy Now" },
        ]
  );

  // Sync local state when sectionData changes (for streaming API data)
  useEffect(() => {
    setLocalTitle(title || "");
    setLocalDescription(description || "");
    if (Array.isArray(features) && features.length > 0) {
      setLocalFeatures(features);
    }
  }, [id, title, description, features]);

  // Merge section-specific styles with parent styles
  const sectionStyleSettings = sectionData?.styleSettings || {};
  const effectiveStyleSettings = {
    ...styleSettings,  // Parent styles (defaults)
    ...sectionStyleSettings  // Section-specific overrides
  };

  const handleSave = () => {
    const currentSection = sections.find(section => section.id === id) || {};
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
      features: localFeatures,
      styleSettings: {
        ...(currentSection.styleSettings || {}) // Preserve existing styleSettings
      }
    });
    setIsEditing(false);
  };

  const handleFeatureUpdate = (index, field, value) => {
    const updated = [...localFeatures];
    updated[index] = { ...updated[index], [field]: value };
    setLocalFeatures(updated);
  };

  // Split description by semicolon and trim
  const parseBenefits = (desc) => {
    if (!desc) return [];
    return desc.split(";").map((b) => b.trim()).filter((b) => b.length > 0);
  };


  if (isEditing && !previewMode) {
    return (
      <div className="email-component p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">2 Column 3 Rows Benefits</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Title</label>
          <input
            className="form-control"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            placeholder="Supportive products commonly paired with Lipomap"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Description</label>
          <textarea
            className="form-control"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            placeholder="These formulas are often recommended..."
            rows={3}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Products (3 items)</label>
          {localFeatures.map((feature, index) => (
            <div key={index} className="border p-3 mb-3 rounded">
              <h6 className="mb-2">Product {index + 1}</h6>
              <div className="mb-2">
                <label className="form-label small">Product Title</label>
                <input
                  className="form-control form-control-sm"
                  value={feature.title || ""}
                  onChange={(e) => handleFeatureUpdate(index, "title", e.target.value)}
                  placeholder="Livomap"
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">Benefits (semicolon-separated)</label>
                <textarea
                  className="form-control form-control-sm"
                  value={feature.description || ""}
                  onChange={(e) => handleFeatureUpdate(index, "description", e.target.value)}
                  placeholder="Support against fatty liver; helps detoxify the liver; supports healing"
                  rows={2}
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">Product Image</label>
                <EmailImageUpload
                  initialImage={feature.image || feature.image_placeholder}
                  onImageUpload={(img) => {
                    console.log("2COL_3ROWS_Benefits: Image uploaded for feature", index, img);
                    // Update both fields in a single operation to avoid double renders
                    const updated = [...localFeatures];
                    updated[index] = { 
                      ...updated[index], 
                      image: img,
                      image_placeholder: img 
                    };
                    setLocalFeatures(updated);
                    // Also update context immediately
                    updateSection(id, {
                      id,
                      component_name,
                      title: localTitle,
                      description: localDescription,
                      features: updated,
                    });
                  }}
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">Button Text</label>
                <input
                  className="form-control form-control-sm"
                  value={feature.cta || "Buy Now"}
                  onChange={(e) => handleFeatureUpdate(index, "cta", e.target.value)}
                  placeholder="Buy Now"
                />
              </div>
              <div className="mb-2">
                <label className="form-label small">Button Link</label>
                <input
                  className="form-control form-control-sm"
                  value={feature.link || ""}
                  onChange={(e) => handleFeatureUpdate(index, "link", e.target.value)}
                  placeholder="Enter link URL"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const primaryColor = effectiveStyleSettings?.primaryColor || "#333333";
  const secondaryColor = effectiveStyleSettings?.secondaryColor || "#a1894c";
  const fontFamily = effectiveStyleSettings?.fontFamily || "inherit";
  const backgroundColor = effectiveStyleSettings?.backgroundColor || "#ffffff";
  return (
    <>
      {!previewMode && (
        <div className="email-component mb-3">
          <div className="component-controls mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <StyleControls 
              id={id} 
              componentName={component_name}
              styleSettings={styleSettings}
              previewMode={previewMode}
            />
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily,
          backgroundColor: backgroundColor,
        }}
      >
        <tbody>
          {/* Title and Description */}
          {(localTitle || localDescription) && (
            <tr>
              <td
                align="center"
                style={{
                  padding: "24px 16px 16px 16px",
                  fontFamily,
                }}
              >
                {localTitle && (
                  <h2
                    style={{
                      color: primaryColor,
                      fontSize: "24px",
                      fontWeight: "600",
                      margin: "0 0 12px 0",
                      fontFamily,
                    }}
                  >
                    {localTitle}
                  </h2>
                )}
                {localDescription && (
                  <p
                    style={{
                      color: primaryColor,
                      fontSize: "16px",
                      lineHeight: "1.6",
                      margin: "0",
                      fontFamily,
                    }}
                  >
                    {localDescription}
                  </p>
                )}
              </td>
            </tr>
          )}

          {/* Product Rows */}
          {localFeatures.slice(0, 3).map((feature, index) => {
            const benefits = parseBenefits(feature.description || "");
            const imageUrl = getImageUrl(feature.image || feature.image_placeholder);
            const isImageLeft = index % 2 === 0; // Row 0, 2: image left; Row 1: image right

            return (
              <tr key={index}>
                <td
                  style={{
                    padding: "16px",
                    fontFamily,
                  }}
                >
                  <table
                    width="100%"
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    style={{
                      fontFamily,
                    }}
                  >
                    <tbody>
                      <tr>
                        {/* When image is on left: Image first, then Content */}
                        {isImageLeft ? (
                          <>
                            <td
                              width="50%"
                              valign="middle"
                              align="center"
                              style={{
                                padding: "0 8px 0 0",
                                fontFamily,
                              }}
                            >
                              <img
                                src={imageUrl || "/img-preview.svg"}
                                alt={feature.title || `Product ${index + 1}`}
                                width="200"
                                height="250"
                                style={{
                                  width: "200px",
                                  height: "250px",
                                  display: "block",
                                  backgroundColor: "#ffffff",
                                  margin: "0 auto",
                                }}
                              />
                            </td>
                            <td
                              width="50%"
                              valign="top"
                              style={{
                                padding: "0 0 0 8px",
                                fontFamily,
                              }}
                            >
                              <table
                                width="100%"
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                                style={{
                                  fontFamily,
                                }}
                              >
                                <tbody>
                                  {feature.title && (
                                    <tr>
                                      <td
                                        style={{
                                          paddingBottom: "12px",
                                          fontFamily,
                                        }}
                                      >
                                        <h3
                                          style={{
                                            color: secondaryColor,
                                            fontSize: "20px",
                                            fontWeight: "600",
                                            margin: "0",
                                            fontFamily,
                                          }}
                                        >
                                          {feature.title}
                                        </h3>
                                      </td>
                                    </tr>
                                  )}
                                  {benefits.length > 0 && (
                                    <tr>
                                      <td
                                        style={{
                                          paddingBottom: "16px",
                                          fontFamily,
                                        }}
                                      >
                                        <ul
                                          style={{
                                            color: primaryColor,
                                            fontSize: "14px",
                                            lineHeight: "1.8",
                                            paddingLeft: "20px",
                                            margin: "0",
                                            fontFamily,
                                          }}
                                        >
                                          {benefits.map((benefit, bIndex) => (
                                            <li key={bIndex} style={{ marginBottom: "4px" }}>
                                              {benefit}
                                            </li>
                                          ))}
                                        </ul>
                                      </td>
                                    </tr>
                                  )}
                                  {(feature.cta || feature.link) && (
                                    <tr>
                                      <td
                                        align="left"
                                        style={{
                                          fontFamily,
                                          paddingTop: "8px",
                                        }}
                                      >
                                        <table
                                          cellPadding="0"
                                          cellSpacing="0"
                                          border="0"
                                          align="left"
                                          style={{
                                            fontFamily,
                                          }}
                                        >
                                          <tr>
                                            <td
                                              bgcolor={secondaryColor}
                                              style={{
                                                backgroundColor: secondaryColor,
                                                borderRadius: "4px",
                                                fontFamily,
                                              }}
                                            >
                                              <a
                                                href={feature.link || " "}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  display: "block",
                                                  padding: "12px 24px",
                                                  fontSize: "14px",
                                                  fontWeight: "600",
                                                  color: "#ffffff",
                                                  textDecoration: "none",
                                                  fontFamily: fontFamily || "Arial, Helvetica, sans-serif",
                                                }}
                                              >
                                                {feature.cta || "Buy Now"}
                                              </a>
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </td>
                          </>
                        ) : (
                          <>
                            {/* When image is on right: Content first, then Image */}
                            <td
                              width="50%"
                              valign="top"
                              style={{
                                padding: "0 8px 0 0",
                                fontFamily,
                              }}
                            >
                              <table
                                width="100%"
                                cellPadding="0"
                                cellSpacing="0"
                                border="0"
                                style={{
                                  fontFamily,
                                }}
                              >
                                <tbody>
                                  {feature.title && (
                                    <tr>
                                      <td
                                        style={{
                                          paddingBottom: "12px",
                                          fontFamily,
                                        }}
                                      >
                                        <h3
                                          style={{
                                            color: secondaryColor,
                                            fontSize: "20px",
                                            fontWeight: "600",
                                            margin: "0",
                                            fontFamily,
                                          }}
                                        >
                                          {feature.title}
                                        </h3>
                                      </td>
                                    </tr>
                                  )}
                                  {benefits.length > 0 && (
                                    <tr>
                                      <td
                                        style={{
                                          paddingBottom: "16px",
                                          fontFamily,
                                        }}
                                      >
                                        <ul
                                          style={{
                                            color: primaryColor,
                                            fontSize: "14px",
                                            lineHeight: "1.8",
                                            paddingLeft: "20px",
                                            margin: "0",
                                            fontFamily,
                                          }}
                                        >
                                          {benefits.map((benefit, bIndex) => (
                                            <li key={bIndex} style={{ marginBottom: "4px" }}>
                                              {benefit}
                                            </li>
                                          ))}
                                        </ul>
                                      </td>
                                    </tr>
                                  )}
                                  {(feature.cta || feature.link) && (
                                    <tr>
                                      <td
                                        align="left"
                                        style={{
                                          fontFamily,
                                          paddingTop: "8px",
                                        }}
                                      >
                                        <table
                                          cellPadding="0"
                                          cellSpacing="0"
                                          border="0"
                                          align="left"
                                          style={{
                                            fontFamily,
                                          }}
                                        >
                                          <tr>
                                            <td
                                              bgcolor={secondaryColor}
                                              style={{
                                                backgroundColor: secondaryColor,
                                                borderRadius: "4px",
                                                fontFamily,
                                              }}
                                            >
                                              <a
                                                href={feature.link || " "}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  display: "block",
                                                  padding: "12px 24px",
                                                  fontSize: "14px",
                                                  fontWeight: "600",
                                                  color: "#ffffff",
                                                  textDecoration: "none",
                                                  fontFamily: fontFamily || "Arial, Helvetica, sans-serif",
                                                }}
                                              >
                                                {feature.cta || "Buy Now"}
                                              </a>
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </td>
                            <td
                              width="50%"
                              valign="middle"
                              align="center"
                              style={{
                                padding: "0 0 0 8px",
                                fontFamily,
                              }}
                            >
                              <img
                                src={imageUrl || "/img-preview.svg"}
                                alt={feature.title || `Product ${index + 1}`}
                                width="200"
                                height="250"
                                style={{
                                  width: "200px",
                                  height: "250px",
                                  display: "block",
                                  backgroundColor: "#ffffff",
                                  margin: "0 auto",
                                }}
                              />
                            </td>
                          </>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default TwoColThreeRowsBenefits;

